import { Api, currentConfig } from "../constants";
import type { OpenApiEndpointsWithMethod, OpenApiOperation, OpenApiPaths } from "./http-client-openapi.types";
import { buildEndpointUrl } from "./http-client-url-builder";
import type { FetchOptions, HttpClientError, HttpClientFetchOptions, HttpClientResponse, HttpClientResult } from "./http-client.types";
import { enqueueRefreshToken } from "./refresh-token-queue";

export interface HttpClientOptions {
  onRetryFail?: (response: Response) => void;
  tenantId: string;
}

export class HttpClient<P extends OpenApiPaths> {
  readonly baseUrl: string;

  constructor(
    baseUrl: string,
    readonly options: HttpClientOptions,
  ) {
    if (!baseUrl && !currentConfig.apiUrl)
      throw new Error("Base URL must be provided either via argument or the API_URL environment variable.");

    this.baseUrl = baseUrl || currentConfig.apiUrl;
  }

  async get<E extends OpenApiEndpointsWithMethod<P, "get">, O extends OpenApiOperation = NonNullable<P[E]["get"]>>(
    endpoint: E,
    options: HttpClientFetchOptions<O>,
  ): Promise<HttpClientResult<O>> {
    return await this.fetchWithOptions<O>(endpoint, "get", options);
  }

  async post<E extends OpenApiEndpointsWithMethod<P, "post">, O extends OpenApiOperation = NonNullable<P[E]["post"]>>(
    endpoint: E,
    options: HttpClientFetchOptions<O>,
  ): Promise<HttpClientResult<O>> {
    return await this.fetchWithOptions<O>(endpoint, "post", options);
  }

  async put<E extends OpenApiEndpointsWithMethod<P, "put">, O extends OpenApiOperation = NonNullable<P[E]["put"]>>(
    endpoint: E,
    options: HttpClientFetchOptions<O>,
  ): Promise<HttpClientResult<O>> {
    return await this.fetchWithOptions<O>(endpoint, "put", options);
  }

  async patch<E extends OpenApiEndpointsWithMethod<P, "patch">, O extends OpenApiOperation = NonNullable<P[E]["patch"]>>(
    endpoint: E,
    options: HttpClientFetchOptions<O>,
  ): Promise<HttpClientResult<O>> {
    return await this.fetchWithOptions<O>(endpoint, "patch", options);
  }

  async delete<E extends OpenApiEndpointsWithMethod<P, "delete">, O extends OpenApiOperation = NonNullable<P[E]["delete"]>>(
    endpoint: E,
    options: HttpClientFetchOptions<O>,
  ): Promise<HttpClientResult<O>> {
    return await this.fetchWithOptions<O>(endpoint, "delete", options);
  }

  private async fetchWithOptions<O extends OpenApiOperation>(endpoint: string, method: string, options: HttpClientFetchOptions<O>) {
    const { path, query, body, fetchOptions } = options as {
      path?: Record<string, string | number>;
      query?: object;
      body?: object;
      fetchOptions?: FetchOptions;
    };

    return await this.fetch<O>(endpoint, method, path ?? {}, query ?? {}, body, fetchOptions);
  }

  private async fetch<O extends OpenApiOperation>(
    endpoint: string,
    method: string,
    pathVariables: Record<string, string | number>,
    query: object,
    body?: object,
    fetchOptions?: FetchOptions,
  ): Promise<HttpClientResult<O>> {
    const url = buildEndpointUrl(this.baseUrl, endpoint, pathVariables ?? {}, query);

    const requestInit: RequestInit = {
      method: method.toUpperCase(),
      ...(body && {
        body: JSON.stringify(body),
      }),
      ...fetchOptions,
      headers: {
        ...(this.options?.tenantId !== "" && {
          "X-Tenant-Id": this.options.tenantId,
        }),
        ...(body && {
          "Content-Type": "application/json",
        }),
        ...this.parseHeaders(fetchOptions?.headers),
      },
      credentials: "include",
    };

    try {
      const response = await fetch(url, requestInit);
      const hasRefreshingEnabled = fetchOptions?.withRefreshing ?? true;

      if (hasRefreshingEnabled && response.status === 403) {
        const refreshResponse = await this.requestRefreshToken();

        if (refreshResponse.ok) {
          const newResponse = await fetch(url, requestInit);
          return this.handleResponse(newResponse, true);
        }

        this.options?.onRetryFail?.(refreshResponse);
      }

      return this.handleResponse(response);
    } catch (e) {
      return {
        error: {
          exception: e instanceof Error ? e : new Error("Unknown error occurred", { cause: e }),
        },
      };
    }
  }

  private async handleResponse<O extends OpenApiOperation>(response: Response, isRetry?: boolean) {
    if (response.ok) {
      return {
        response: {
          status: response.status,
          body: await this.getResponseData(response),
        } as unknown as HttpClientResponse<O>,
        error: undefined,
      };
    }

    if (isRetry) this.options?.onRetryFail?.(response);

    return {
      error: {
        response: {
          status: response.status,
          body: await this.getResponseData(response),
        },
      } as unknown as HttpClientError<O>,
    };
  }

  private async requestRefreshToken() {
    return await enqueueRefreshToken(() =>
      fetch(buildEndpointUrl(Api.Account, "/refresh", {}, {}), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      }),
    );
  }

  private async getResponseData(response: Response) {
    const contentType = response.headers.get("content-type");

    if (!response.body) {
      return null;
    }

    try {
      const buffer = await response.arrayBuffer();

      if (buffer.byteLength === 0) {
        return null;
      }

      if (contentType?.includes("application/json")) {
        const text = new TextDecoder().decode(buffer);
        return JSON.parse(text);
      }

      if (contentType?.includes("text/html") || contentType?.includes("text/plain")) {
        return new TextDecoder().decode(buffer);
      }

      return new Blob([buffer], { type: contentType || undefined });
    } catch (error) {
      console.error("Error processing response:", error);
      return null;
    }
  }

  private parseHeaders(headers?: HeadersInit) {
    const entries = headers instanceof Headers ? headers.entries() : new Headers(headers).entries();
    return Object.fromEntries(Array.from(entries));
  }
}
